import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Settings, User, Sliders, Users, ShieldAlert, Plug, X } from "lucide-react";
import { useAuth } from "../../context/AuthProvider";
import { AnimatePresence, motion } from "framer-motion";
import Profil from "./components/Profil";
import Preferences from "./components/Preferences";
import Equipe from "./components/Equipe";
import Securite from "./components/Securite";
import Integrations from "./components/Integrations";

export default function SettingsModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("profil");

  const { user } = useAuth();
  const userRole = user?.jobTitle || "Utilisateur";
  
  const roles = user?.roles || [];
  
  const hasEquipeAccess = roles.includes("SYSADMIN") || roles.includes("ADMIN") || ["Administrateur Système", "Administrateur"].includes(userRole);
  const hasAdvancedAccess = roles.includes("SYSADMIN") || roles.includes("ADMIN") || roles.includes("MODERATOR") || ["Administrateur Système", "Administrateur", "Modérateur"].includes(userRole);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const tabs = [
    { id: "profil", label: "Mon Profil", icon: <User className="w-4 h-4" /> },
    { id: "preferences", label: "Préférences", icon: <Sliders className="w-4 h-4" /> },
    ...(hasEquipeAccess ? [
      { id: "equipe", label: "Équipe & Rôles", icon: <Users className="w-4 h-4" /> }
    ] : []),
    ...(hasAdvancedAccess ? [
      { id: "securite", label: "Sécurité & Audit", icon: <ShieldAlert className="w-4 h-4" /> },
      { id: "integrations", label: "Intégrations & API", icon: <Plug className="w-4 h-4" /> }
    ] : [])
  ];

  const renderTabContent = () => {
    switch (selectedTab) {
      case "profil": return <Profil />;
      case "preferences": return <Preferences />;
      case "equipe": return hasEquipeAccess ? <Equipe /> : <Profil />;
      case "securite": return hasAdvancedAccess ? <Securite /> : <Profil />;
      case "integrations": return hasAdvancedAccess ? <Integrations /> : <Profil />;
      default: return <Profil />;
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-full hover:bg-workspace transition text-muted hover:text-primary"
        title="Paramètres"
      >
        <Settings className="w-6 h-6" strokeWidth={1.5} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm"
              aria-hidden="true"
            />

            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel
                as={motion.div}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className={`w-full transition-[max-width] duration-500 ease-in-out ${
                  (selectedTab === 'equipe' || selectedTab === 'securite') ? 'max-w-[95vw]' : 'max-w-4xl'
                } h-[85vh] flex flex-col md:flex-row rounded-xl bg-surface shadow-hp-2xl overflow-hidden border border-workspace`}
              >
                {/* Sidebar */}
                <div className="w-full md:w-64 bg-surface-muted border-r border-workspace flex flex-col shrink-0 transition-colors">
                  <div className="p-6 pb-2 border-b border-workspace md:border-b-0">
                    <h3 className="text-xl font-bold text-primary flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary-500" />
                      Paramètres
                    </h3>
                  </div>
                  <div className="p-4 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
                    <ul className="space-y-1">
                      {tabs.map((tab) => (
                        <li key={tab.id}>
                          <button
                            onClick={() => setSelectedTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition relative z-10 ${selectedTab === tab.id ? "text-primary" : "text-muted hover:text-primary hover:bg-workspace/50"
                              }`}
                          >
                            {selectedTab === tab.id && (
                              <motion.div
                                layoutId="activeTabSettings"
                                className="absolute inset-0 bg-surface rounded-lg shadow-sm border border-workspace -z-10"
                                initial={false}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                              />
                            )}
                            <span className={selectedTab === tab.id ? "text-primary-500" : ""}>{tab.icon}</span>
                            {tab.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 bg-surface relative flex flex-col min-w-0 min-h-0">
                  {/* Header mobile close button (optional but good) */}
                  <div className="absolute top-4 right-4 z-50">
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 bg-surface-muted hover:bg-workspace rounded-full text-muted hover:text-primary transition border border-workspace shadow-sm"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedTab}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="flex-1 min-h-0 overflow-hidden"
                    >
                      {renderTabContent()}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        )}
      </AnimatePresence>
    </>
  );
}
